import { ReactNode, ReactElement } from 'react';
import { useBusiness } from 'hooks/useBusiness';
import { useLocalization } from 'hooks/useLocalization';
import { Box, Stack, Typography } from '@mui/material'
import {
	Info as InfoIcon,
	AccountCircle as AccountCircleIcon,
	People as PeopleIcon,
	Person as PersonIcon,
	AttachMoney as AttachMoneyIcon,
	ShoppingCart as ShoppingCartIcon,
	FlightLand as FlightLandIcon
} from '@mui/icons-material';
import { formatNumberWithSpacesAndRounding } from 'utils/formatUtils';
import 'components/booking/style.css';
import { Booking, BookingCartItem, BookingCartItemAddon } from 'interfaces/BookingInterfaces';
import repeatComponent from 'utils/repeatComponent';

interface BookingInfoSectionProps {
	titleCode: string;
	icon: ReactNode;
	titleInfo?: string;
	rows: any[];
}

function BookingInfoSection({ titleCode, icon, titleInfo, rows }: BookingInfoSectionProps) {
	const { getStr } = useLocalization();

	return (
		<div className="booking-info-section">
			<Box
				className="booking-info-section-header"
				sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
			>
				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>{icon}</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>{getStr(titleCode)}</Box>
				</Box>
				{titleInfo && (
					<Box sx={{ textAlign: 'right', ml: 'auto', fontWeight: 'bold' }}>{titleInfo}</Box>
				)}
			</Box>
			<Box className="booking-info-section-content">
				<Stack spacing={1}>
					{rows
					.map((row, index) => {
						if (row.leftContent && row.rightContent) {
							return (
								<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
									<Box>{row.leftContent}</Box>
									<Box sx={{ flexGrow: 1, textAlign: 'right' }}>{row.rightContent}</Box>
								</Box>
							)
						}

						if (row.fullRowContent) {
							return (
								<Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
									{row.fullRowContent}
								</Box>
							)
						}

						return null;
					})}
				</Stack>
			</Box>
		</div>
	);
}

interface BookingViewProps {
	booking: Booking;    
}

interface PassengerSectionRow {
	
}

function BookingView({ booking }: BookingViewProps) {
	const { getStr } = useLocalization();
	const { getBizConfigVar } = useBusiness();
	const paxPickupConfig = getBizConfigVar('paxPickup');
	const useLodgings = getBizConfigVar('useLodgings');
	const usePickupLocations = getBizConfigVar('usePickupLocations');

	let statusColor;
	let statusTitle;

	if (booking && booking.status) {
		statusTitle = getStr('BOOKING_STATUS_TITLE_' + booking.status);

		switch (booking.status) {
			case 'N': statusColor = '#9d9f07'; break;
			case 'U': statusColor = '#c71c1c'; break;
			case 'D': statusColor = '#2741c4'; break;
			case 'P': statusColor = '#047701'; break;
			case 'C': statusColor = '#6d6d6d'; break;
		}
	}

	let passengerSectionRows: PassengerSectionRow[] = [
		{
			leftContent: getStr('Amount_of_passengers'),
			rightContent: booking.paxQty
		},
		{
			fullRowContent: 
				<div>
					{Array.from({ length: booking.paxQty }, (_, index) => (
						<PersonIcon key={index} />
					))}
				</div>
		}
	];

	if (usePickupLocations) { //Show pickup location
		passengerSectionRows.push({
			leftContent: getStr('Pickup_location'),
			rightContent: booking.pickupLocation !== null ? booking.pickupLocation.name : (<span style={{ color: 'red' }}>{getStr('UNSET').toUpperCase()}</span>)
		});
	}

	if (paxPickupConfig.mode === 1 || (paxPickupConfig.mode === 2 && paxPickupConfig.useCollectLodging)) { //Show lodging
		passengerSectionRows.push({
			leftContent: getStr('Lodging'),
			rightContent: booking.lodging.id ? (booking.lodging.name ? booking.lodging.name : <span style={{ color: 'red' }}>{getStr('UNSET')}</span>) : <span style={{ color: 'red' }}>{getStr('UNSET')}</span>
		});
	}

	const arrivalDepartureRowItems = [
		{ titleCode: 'Arrival_date', value: booking.arrival.dateYmd },
		{ titleCode: 'Departure_date', value: booking.departure.dateYmd },
		{ titleCode: 'Arrival_time', value: booking.arrival.time },
		{ titleCode: 'Departure_time', value: booking.departure.time },
		{ titleCode: 'Arr_transp_no_ABBREV', value: booking.arrival.transNo },
		{ titleCode: 'Dep_transp_no_ABBREV', value: booking.departure.transNo },
	];

	const arrivalDepartureRows = arrivalDepartureRowItems.map(rowItem => {
		if (rowItem.value) {
			return {
				leftContent: getStr(rowItem.titleCode),
				rightContent: rowItem.value
			};
		} else {
			return null;
		}
	}).filter(row => row !== null);

	// Sort cart items by date and time, placing custom items without date last
	const sortedCartItems = booking.cartItems.sort((a: BookingCartItem, b: BookingCartItem) => {
		// Custom items without date should be placed last
		if (a.typeCode === 'custom' && !a.setDateTime && b.typeCode === 'custom' && !b.setDateTime) return 0;
		if (a.typeCode === 'custom' && !a.setDateTime) return 1;
		if (b.typeCode === 'custom' && !b.setDateTime) return -1;
	
		const dateA = new Date(`${a.date?.ymd}T${a.time || '00:00'}`);
		const dateB = new Date(`${b.date?.ymd}T${b.time || '00:00'}`);
	
		return dateA.getTime() - dateB.getTime();
	});

	const totalsRows: any[] = [
		{
			leftContent: getStr('Per_person'),
			rightContent: formatNumberWithSpacesAndRounding(booking.total / booking.paxQty)
		},
		{
			leftContent: `${getStr('Total')} (${booking.paxQty} ${getStr(booking.paxQty === 1 ? 'person' : 'people')})`,
			rightContent: formatNumberWithSpacesAndRounding(booking.total)
		}
	];

	if (booking.totalPaid > 0) {
		totalsRows.push({
			leftContent: getStr('Total_paid'),
			rightContent: formatNumberWithSpacesAndRounding(booking.totalPaid)
		});
		
		if (booking.totalPaid < booking.total) {
			totalsRows.push({
				leftContent: getStr('Left_to_pay'),
				rightContent: formatNumberWithSpacesAndRounding(booking.total - booking.totalPaid)
			});
		}
	}
	
	return(
		<div className="booking-wrapper">
			<div className="booking-header">
				<Typography sx={{ fontSize: '1.5em', fontWeight: 'bold' }}>{ booking.name } x { booking.paxQty}</Typography>
				<div style={{ fontSize: '0.8em' }}>{getStr('ID')}# { booking.bookingNumber }</div>
			</div>
			<BookingInfoSection
				titleCode="Booking"
				icon={<InfoIcon />}
				rows={
					[
						{
							leftContent: getStr('Status'),
							rightContent: <span style={{ color: statusColor, fontWeight: 'bold' }}>{statusTitle?.toUpperCase()}</span>
						},
						{
							leftContent: getStr('Discount'),
							rightContent: booking.discount ? `${booking.discount}%` : null
						},
						{
							leftContent: getStr('Created_on_TIME'),
							rightContent: booking.createdOnStr
						},
						{
							leftContent: getStr('Created_by_PERSON'),
							rightContent: booking.createdByUser.id === booking.contactUser.id ? <i>{getStr('Client')}</i> : booking.createdByUser.name
						}
					]
				}
			/>
			<BookingInfoSection
				titleCode="Contact"
				icon={<AccountCircleIcon />}
				rows={
					[
						{
							leftContent: getStr('Name'),
							rightContent: booking.contactUser.name
						},
						{
							leftContent: getStr('Email'),
							rightContent: booking.contactUser.email
						},
						{
							leftContent: getStr('Country'),
							rightContent: booking.contactUser.countryTitle
						},
						{
							leftContent: getStr('Phone'),
							rightContent: booking.contactUser.phones.map((phone: any, index: number) => {
								if (phone.countryNumber) {
									return <div key={index}>{`+${phone.countryNumber} ${phone.number}`}</div>;
								} else {
									return <div key={index}>{phone.number}</div>;
								}
							})
						}
					]
				}
			/>
			<BookingInfoSection
				titleCode="Passengers"
				icon={<PeopleIcon />}
				rows={passengerSectionRows}
			/>
			{arrivalDepartureRows.length > 0 && (
				<BookingInfoSection
					titleCode="Arrival_and_departure"
					icon={<FlightLandIcon />}
					rows={arrivalDepartureRows}
				/>
			)}
			<BookingInfoSection
				titleCode="Cart"
				titleInfo={booking.currencyCode + ' ' + getStr('PER_PERSON_ABBREV')}
				icon={<ShoppingCartIcon />}
				rows={
					sortedCartItems.map((cartItem: BookingCartItem, index: number) => {
						let typeTitle;
						switch (cartItem.typeCode) {
							case 'activity':
								typeTitle = getStr('ACTIVITY_TYPE_TITLE_' + cartItem.activityType);
								break;

							case 'custom':
								typeTitle = getStr('ACTIVITY_TYPE_TITLE_' + cartItem.typeCode);
								break;
						}

						let typeColor;

						switch (cartItem.typeCode) {
							case 'custom':
								typeColor = '#9f4dd9';
								break;

							case 'activity':
								typeColor = cartItem.activityType === 'private' ? '#1c61ee' : '#349d2a';
								break;

							default:
								throw new Error('Unknown cart item type');
						}

						return {
							fullRowContent:
								<Box sx={{ width: '100%' }}>
									<Box sx={{ fontSize: '0.8em', lineHeight: '1em', color: typeColor }}>{typeTitle}</Box>
									<Box display="flex" flexDirection="row">
										<Box>{cartItem.title}</Box>
										<Box sx={{ flexGrow: 1, textAlign: 'right' }}>{formatNumberWithSpacesAndRounding(cartItem.total / booking.paxQty)}</Box>
									</Box>
									{cartItem.addons?.map((addon: BookingCartItemAddon, index: number) => (
										<Box key={index} display="flex" flexDirection="row" sx={{ fontSize: '0.8em', lineHeight: '1em' }}>
											<Box>+ {addon.title}</Box>
											<Box sx={{ flexGrow: 1, textAlign: 'right' }}>{formatNumberWithSpacesAndRounding(addon.total / booking.paxQty)}</Box>
										</Box>
									))}
									{
										(
											cartItem.typeCode === 'activity' ||
											(
												cartItem.typeCode === 'custom' &&
												cartItem.setDateTime
											)
										) && (
											<Box sx={{ fontSize: '0.8em', lineHeight: '1.2em' }}>
												<b>{cartItem.date.formatted ? cartItem.date.formatted : <span style={{ color: 'red' }}>{getStr('Date_unset').toUpperCase()}</span>}</b> - {cartItem.time ? cartItem.time : <span style={{ color: 'red' }}>{getStr('Time_unset').toUpperCase()}</span>}
											</Box>
										)
									}
								</Box>
						}
					})
				}
			/>
			{
				booking.bookedLodging && (
					<BookingInfoSection
						titleCode="Booked_lodging"
						titleInfo={booking.currencyCode + ' ' + getStr('PER_PERSON_ABBREV')}
						icon={<PeopleIcon />}
						rows={
							[
								{
									leftContent: booking.bookedLodging.name,
									rightContent: formatNumberWithSpacesAndRounding(booking.bookedLodging.priceTotalBookingCurrency / booking.paxQty)
								},
								...booking.bookedLodging.rooms.map((room, index) => ({
									leftContent: getStr('Room') + ' ' + (index + 1),
									rightContent: repeatComponent(room.pax, <PersonIcon />)
								})
								)
							]
						}
					/>
				)
			}
			<BookingInfoSection
				titleCode="Totals"
				titleInfo={booking.currencyCode}
				icon={<AttachMoneyIcon />}
				rows={totalsRows}
			/>
		</div>
	);
}

export default BookingView;